import Api from '../Api';
import {
  fetchAutofillDataForSelectedCountry,
  stripJsonFromPastedText,
  normalizeContactDigits,
  findCityById,
} from './organizationAutofill';

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

/**
 * Справочник для файла и «Преобразовать»: города выбранной страны + категории мероприятий.
 */
export async function fetchEventAutofillDataForSelectedCountry(countryId) {
  const base = await fetchAutofillDataForSelectedCountry(countryId);
  const { data } = await Api.getEventCategories();
  return {
    ...base,
    eventCategories: data?.categories || [],
  };
}

function findEventCategoryById(eventCategories, id) {
  if (!id) return null;
  return eventCategories.find((c) => c._id === id) || null;
}

/**
 * Частичное заполнение формы «Добавить мероприятие».
 */
export function parseEventAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  /** Ключ есть и значение не null — null значит «поле не трогать, не валидировать» */
  const take = (k) => has(k) && data[k] !== null;
  const { citiesByCountryId, eventCategories } = staticData;

  const patches = {};

  if (take('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (take('name')) patches.name = String(data.name).trim();
  if (take('specialization')) patches.specialization = String(data.specialization).trim();
  if (take('holding_date')) patches.holdingDate = String(data.holding_date).trim();
  if (take('date_start')) patches.dateStart = String(data.date_start).trim();
  if (take('date_end')) patches.dateEnd = String(data.date_end).trim();
  if (take('address')) patches.address = String(data.address).trim();
  if (take('description')) patches.description = String(data.description).trim();

  if (take('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k) && data.contacts[k] !== null) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  if (take('events_category_id')) {
    const cat = findEventCategoryById(eventCategories, data.events_category_id);
    if (!cat) {
      return { error: 'Категория мероприятия не найдена по events_category_id.' };
    }
    patches.eventCategory = cat;
  }

  if (take('city_id')) {
    const city = findCityById(citiesByCountryId, data.city_id);
    if (!city) {
      return { error: 'Город не найден по city_id.' };
    }
    patches.city = city;
  }

  if (take('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
    patches.coordinates = {
      lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
      lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
      is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
    };
  }

  return { patches };
}

export function buildEventAutofillReferenceFileContent(staticPayload) {
  const {
    selectedCountry,
    citiesByCountryId,
    eventCategories,
  } = staticPayload;

  if (!selectedCountry?._id) {
    throw new Error('selectedCountry is required');
  }

  const countryId = selectedCountry._id;
  const cities = citiesByCountryId[countryId] || [];

  const referenceData = {
    selected_country: { _id: selectedCountry._id, name: selectedCountry.name },
    cities: cities.map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || countryId,
    })),
    event_categories: eventCategories
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((c) => ({
        _id: c._id,
        name: c.name,
        sort: c.sort,
      })),
  };

  const schemaDescription = {
    description: 'Один JSON-объект для формы «Добавить мероприятие». Файл для одной страны: selected_country; в cities — только города этой страны. event_categories — глобальный список. В JSON пиши только _id из REFERENCE_DATA.',
    fields: {
      owner_id: '24-символьный hex ID владельца',
      name: 'Название мероприятия',
      events_category_id: '_id из referenceData.event_categories',
      specialization: 'Обязательно, если выбрана категория с name «Другое»; иначе по желанию',
      holding_date: 'Строка для отображения (любой формат текста о дате/времени проведения)',
      date_start: 'YYYY-MM-DD или пусто — влияет на сортировку в приложении',
      date_end: 'YYYY-MM-DD или пусто',
      city_id: '_id из REFERENCE_DATA.cities (только города selected_country; в списке нет других стран)',
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      contacts: 'phone_number, whatsapp, viber — только цифры подряд; остальные поля — строки',
      description: 'строка',
    },
    json_syntax: 'Сухой plain text: один валидный JSON, как JSON.stringify(объект). Только символы JSON. Без ссылок [текст](url), без URL как отдельных блоков, без HTML/Markdown и стилей чата, без вступлений. Допустимы отступы, как JSON.stringify(obj, null, 2).',
    note: 'Телефоны в contacts.phone_number, whatsapp, viber — только цифры. Если категория «Другое» — обязательно specialization.',
  };

  const prompt = [
    'Ты — помощник по заполнению карточки мероприятия в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала человек пришлёт тебе этот файл одним сообщением. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь ровно: «Задача ясна, отправь текст для преобразования». Дальше жди следующих сообщений.',
    '— Когда пришлют «сухой» текст о мероприятии, сформируй JSON по схеме.',
    '',
    'REFERENCE_DATA: selected_country и cities — только эта одна выбранная при создании файла страна; event_categories — категории мероприятий. Сопоставляй по name, в JSON пиши только _id; city_id — только из cities.',
    '',
    'КРИТИЧНО — категория (events_category_id):',
    '— Только строка _id из referenceData.event_categories. Не подставляй название категории вместо id.',
    '— Если по смыслу подходит категория «Другое», обязательно заполни specialization (своя формулировка типа мероприятия).',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— Только цифры подряд; из +7 (999) 123-45-67 → 79991234567.',
    '— instagram, telegram, facebook, website не обрезай до цифр.',
    '',
    'КРИТИЧНО — даты date_start и date_end:',
    '— Формат YYYY-MM-DD (как для input type=date) или пустая строка, если данных нет.',
    '',
    'КРИТИЧНО — формат ответа: самый обычный сухой JSON-текст (plaintext):',
    '— Программа передаст ответ в JSON.parse. Без внутренних/кликабельных ссылок, без markdown-стилей (**жирный**, _курсив_), без HTML. Только символы валидного JSON.',
    '— Один корневой объект, без ```json, без комментариев, без префикса «вот JSON».',
    '— Двойные кавычки, без запятой после последнего поля, экранирование \\" внутри строк.',
    '',
    'Когда пришёл текст для преобразования:',
    '1) Заполни поля из schemaDescription.fields по смыслу текста.',
    '2) city_id — только _id из REFERENCE_DATA.cities; events_category_id — из event_categories.',
    '3) Верни один JSON-текст, пригодный для JSON.parse.',
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение мероприятия (справочник + инструкция для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) В админке при скачивании выбрана одна страна: в selected_country и в cities — только она. Один файл — одна страна по городам.',
    '2) Сохрани этот файл при необходимости.',
    '3) Первым сообщением отправь в чат с языковой моделью весь файл (или блок: SCHEMA_HINT, затем REFERENCE_DATA и инструкция ниже).',
    '4) Дождись: «Задача ясна, отправь текст для преобразования».',
    '5) Отправь сухой текст о мероприятии.',
    '6) Скопируй JSON ответа (JSON.stringify / JSON.parse). В поле «Автозаполнение» выбери ту же страну, нажми «Преобразовать»; иначе _id города не сойдётся.',
    '7) Если ответ ИИ не принимается системой — открой https://jsonviewer.stack.hu/, вставь текст, нажми «Format», исправь структуру при необходимости, скопируй обратно.',
    '',
    prompt,
  ].join('\n');
}
